import React, { useEffect, useState } from 'react';
import { Bell, X, Package, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from '../i18n/LanguageContext';

const BUDGET_READ_STORAGE_KEY = 'smartinventory_read_budget_notifications_v1';
const BUDGET_DISMISSED_STORAGE_KEY = 'smartinventory_dismissed_budget_notifications_v1';

const loadPersistedBudgetReadIds = () => {
  try {
    const raw = localStorage.getItem(BUDGET_READ_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const persistBudgetReadIds = (idSet) => {
  try {
    localStorage.setItem(BUDGET_READ_STORAGE_KEY, JSON.stringify(Array.from(idSet)));
  } catch {
    // Ignore storage failures.
  }
};

const loadPersistedBudgetDismissedIds = () => {
  try {
    const raw = localStorage.getItem(BUDGET_DISMISSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const persistBudgetDismissedIds = (idSet) => {
  try {
    localStorage.setItem(BUDGET_DISMISSED_STORAGE_KEY, JSON.stringify(Array.from(idSet)));
  } catch {
    // Ignore storage failures.
  }
};

function NotificationSystem({ refreshToken = 0 } = {}) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readBudgetNotifications, setReadBudgetNotifications] = useState(() => loadPersistedBudgetReadIds());
  const [dismissedBudgetNotifications, setDismissedBudgetNotifications] = useState(() => loadPersistedBudgetDismissedIds());

  // Charger les notifications au démarrage
  useEffect(() => {
    loadNotifications();
    
    // Vérifier les nouvelles notifications toutes les 30 secondes
    const interval = setInterval(loadNotifications, 30000);

    return () => clearInterval(interval);
  }, [readBudgetNotifications, dismissedBudgetNotifications, refreshToken, t]);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    persistBudgetReadIds(readBudgetNotifications);
  }, [readBudgetNotifications]);

  useEffect(() => {
    persistBudgetDismissedIds(dismissedBudgetNotifications);
  }, [dismissedBudgetNotifications]);

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('✅ Notifications autorisées');
      }
    }
  };

  const loadNotifications = async () => {
    try {
      const [notificationsResponse, budgetResponse] = await Promise.all([
        axios.get('/notifications'),
        axios.get('/budget/monthly-summary').catch(() => ({ data: { alerts: [], month_label: '' } }))
      ]);
      const budgetNotifications = (budgetResponse.data?.alerts || []).map((alert) => ({
        id: `budget-${budgetResponse.data.month_label || 'current'}-${alert.label}-${alert.level}`,
        created_at: new Date().toISOString(),
        isVirtualBudgetAlert: true,
        read: readBudgetNotifications.has(`budget-${budgetResponse.data.month_label || 'current'}-${alert.label}-${alert.level}`),
        data: {
          title: t('notifications.budgetTitle', { month: budgetResponse.data?.month_label || '' }).trim(),
          message: t(`budget.alertMessages.${alert.level}`, { label: t(`budget.alertLabels.${alert.label}`) })
        }
      }));
      const apiNotifications = (notificationsResponse.data || []).map((notif) => ({
        ...notif,
        id: notif.id ?? notif.notification_id,
        read: Boolean(notif.read ?? notif.is_read ?? notif.isRead),
      }));
      const newNotifications = [...budgetNotifications, ...apiNotifications]
        .filter((notif) => !dismissedBudgetNotifications.has(notif.id));
      const newUnreadCount = newNotifications.filter((notif) => !notif.read).length;
      
      // Vérifier s'il y a de nouvelles notifications
      if (newUnreadCount > unreadCount && unreadCount > 0) {
        // Nouvelle notification reçue
        const latestNotif = newNotifications.find((notif) => !notif.read) || newNotifications[0];
        showBrowserNotification(latestNotif);
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      if (String(notificationId).startsWith('budget-')) {
        setDismissedBudgetNotifications((prev) => {
          const next = new Set(prev);
          next.add(notificationId);
          return next;
        });
      } else {
        await axios.delete(`/notifications/${notificationId}`);
      }

      setNotifications((prev) => {
        const nextNotifications = prev.filter((n) => n.id !== notificationId);
        setUnreadCount(nextNotifications.filter((n) => !n.read).length);
        return nextNotifications;
      });

      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Erreur suppression notification:', error);
      toast.error('Suppression impossible pour le moment');
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await axios.delete('/notifications').catch(() => null);

      const budgetIds = notifications
        .filter((n) => String(n.id).startsWith('budget-'))
        .map((n) => n.id);

      if (budgetIds.length) {
        setDismissedBudgetNotifications((prev) => {
          const next = new Set(prev);
          budgetIds.forEach((id) => next.add(id));
          return next;
        });
        setReadBudgetNotifications((prev) => {
          const next = new Set(prev);
          budgetIds.forEach((id) => next.add(id));
          return next;
        });
      }

      setNotifications([]);
      setUnreadCount(0);
      toast.success('Notifications supprimées');
    } catch (error) {
      console.error('Erreur suppression globale notifications:', error);
      toast.error('Suppression globale impossible pour le moment');
    }
  };

  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.data.title, {
        body: notification.data.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notification.data.article_id
      });
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      if (String(notificationId).startsWith('budget-')) {
        setReadBudgetNotifications((prev) => {
          const next = new Set(prev);
          next.add(notificationId);
          return next;
        });
        setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, read: true } : n));
        setUnreadCount((prev) => Math.max(0, prev - 1));
        return;
      }
      await axios.post(`/notifications/${notificationId}/read`);

      setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));

      if ('Notification' in window && typeof Notification.permission !== 'undefined') {
        try {
          if (typeof Notification.get === 'function') {
            const browserNotifs = await Notification.get({ tag: notificationId });
            browserNotifs.forEach((n) => n.close());
          }
        } catch {
          // Browser may not support Notification.get
        }
      }

      await loadNotifications();
    } catch (error) {
      console.error('Erreur marquage lecture:', error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('notifications.now');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffMins < 1440) return t('notifications.hoursAgo', { count: Math.floor(diffMins / 60) });
    return t('notifications.daysAgo', { count: Math.floor(diffMins / 1440) });
  };

  return (
    <div className="relative">
      {/* Bouton de notification */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative"
        data-testid="notifications-toggle"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <Badge data-testid="notifications-badge" className="absolute -top-2 -right-2 min-w-[20px] h-5 text-xs bg-red-500 text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Panel des notifications */}
      {showNotifications && (
        <div data-testid="notifications-panel" className="fixed left-1/2 top-16 z-50 w-[min(24rem,calc(100vw-1.5rem))] -translate-x-1/2 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:translate-x-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[min(75vh,34rem)] overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {t('notifications.title', { count: unreadCount })}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(false)}
                className="h-6 w-6 p-0"
              >
                <X size={14} />
              </Button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <Package size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">{t('notifications.empty')}</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const isRead = Boolean(notif.read);
                return (
                <Card key={notif.id} className={`m-2 border-l-4 ${isRead ? 'border-l-gray-300 bg-gray-50' : 'border-l-red-500 bg-white'}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <AlertTriangle size={20} className={isRead ? 'text-gray-400' : 'text-red-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-medium mb-1 break-words ${isRead ? 'text-gray-600' : 'text-gray-900'}`}>
                          {notif.data.title}
                        </h4>
                        <p className={`text-sm mb-2 break-words ${isRead ? 'text-gray-500' : 'text-gray-700'}`}>
                          {notif.data.message}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-400">
                            {formatTimeAgo(notif.created_at)}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            {!isRead && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => markAsRead(notif.id)}
                                className="text-xs px-2 py-1 h-6"
                              >
                                {t('notifications.markRead')}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteNotification(notif.id)}
                              className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                              aria-label="Supprimer la notification"
                              title="Supprimer"
                              data-testid={`delete-notification-${notif.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const ids = notifications
                        .filter((n) => !n.read && !String(n.id).startsWith('budget-'))
                        .map((n) => n.id);
                      await Promise.all(ids.map((id) => axios.post(`/notifications/${id}/read`)));
                      setReadBudgetNotifications((prev) => {
                        const next = new Set(prev);
                        notifications
                          .filter((n) => !n.read && String(n.id).startsWith('budget-'))
                          .forEach((n) => next.add(n.id));
                        return next;
                      });
                      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
                      setUnreadCount(0);
                      await loadNotifications();
                      if ('Notification' in window && typeof Notification.permission !== 'undefined') {
                        try {
                          if (typeof Notification.get === 'function') {
                            const browserNotifs = await Notification.get();
                            browserNotifs.forEach((n) => n.close());
                          }
                        } catch {
                          // Browser may not support Notification.get
                        }
                      }
                      toast.success(t('notifications.markAllSuccess'));
                    } catch (error) {
                      console.error('Erreur marquage global lecture:', error);
                      toast.error('Action impossible pour le moment');
                    }
                  }}
                  className="w-full text-xs"
                >
                  {t('notifications.markAll')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteAllNotifications}
                  className="w-full text-xs"
                  data-testid="notifications-delete-all"
                >
                  Tout supprimer
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationSystem;